import { useAuth } from '@/hooks/useAuth';
import { useGlobalOrganization } from '@/hooks/useGlobalOrganization';
import { Building2, LogOut, AlertTriangle, WifiOff, RefreshCw, User } from 'lucide-react';
import { LoadingSpinner } from './ui/LoadingSpinner';

function getRoleBadgeClasses(role: string): string {
  switch (role.toLowerCase()) {
    case 'admin':
      return 'bg-purple-100 text-purple-700';
    case 'dispatcher':
      return 'bg-blue-100 text-blue-700';
    case 'technician':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function formatRole(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

export function SelectOrganizationPage() {
  const {
    organizations,
    isLoading,
    organizationsLoadedSuccessfully,
    setSelectedOrgId,
    refetchOrganizations,
  } = useGlobalOrganization();
  const { signOut } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center animate-fade-in">
          <LoadingSpinner />
          <p className="mt-4 text-secondary-600 font-medium">Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (organizations.length === 0) {
    const connectionFailed = !organizationsLoadedSuccessfully;

    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 px-4">
        <div className="max-w-md w-full animate-scale-in text-center">
          <div className="card p-8">
            <div className="flex justify-center mb-4">
              <div className={`rounded-full p-4 ${connectionFailed ? 'bg-amber-100' : 'bg-red-100'}`}>
                {connectionFailed ? (
                  <WifiOff className="h-8 w-8 text-amber-600" />
                ) : (
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                )}
              </div>
            </div>
            {connectionFailed ? (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Can&apos;t Connect to Server</h2>
                <p className="text-gray-600 mb-6">
                  We couldn&apos;t reach the server. Please check your internet connection and try again.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => refetchOrganizations()}
                    className="btn-primary flex items-center justify-center gap-2 w-full"
                    style={{ minHeight: '44px' }}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </button>
                  <button
                    onClick={() => signOut()}
                    className="btn-secondary flex items-center justify-center gap-2 w-full text-red-600 hover:bg-red-50"
                    style={{ minHeight: '44px' }}
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">No Organizations Found</h2>
                <p className="text-gray-600 mb-6">
                  Your account is not associated with any organization. Please contact your administrator.
                </p>
                <button
                  onClick={() => signOut()}
                  className="btn-secondary flex items-center justify-center gap-2 w-full text-red-600 hover:bg-red-50"
                  style={{ minHeight: '44px' }}
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Organization selector
  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 px-4 py-8">
      <div className="max-w-lg w-full animate-scale-in">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-secondary-900 tracking-tight mb-2">
            Service Strike
          </h1>
          <p className="text-secondary-600">
            Select an organization to continue
          </p>
        </div>

        <div className="space-y-3">
          {organizations.map((org) => (
            <button
              key={org.organization_id}
              onClick={() => setSelectedOrgId(org.organization_id)}
              className="card w-full text-left hover:ring-2 hover:ring-primary-500 hover:shadow-md transition-all cursor-pointer active:scale-[0.99]"
              style={{ minHeight: '72px' }}
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 rounded-full bg-primary-100 p-3">
                  <Building2 className="h-5 w-5 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-900 truncate">
                    {org.organization_name}
                  </p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeClasses(org.role)}`}>
                    {formatRole(org.role)}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4">
          <User className="h-5 w-5 flex-shrink-0 text-primary-600" />
          <p className="text-base text-primary-900">
            You can switch organizations any time from the{' '}
            <span className="font-semibold">Profile</span> page.
          </p>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => signOut()}
            className="text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
            style={{ minHeight: '44px' }}
          >
            Sign in with a different account
          </button>
        </div>
      </div>
    </div>
  );
}
